
import { useState, useRef, useEffect } from 'react';
import Button from '../base/Button';

interface Track {
  id: string;
  title: string;
  artist: string;
  album?: string;
  duration: number;
  audioUrl?: string;
  imageUrl?: string;
}

interface MusicPlayerProps {
  currentTrack: Track | null;
  isPlaying: boolean;
  onPlayPause: () => void;
  onNext: () => void;
  onPrevious: () => void;
  onSeek: (time: number) => void;
  currentTime: number;
}

export default function MusicPlayer({
  currentTrack,
  isPlaying,
  onPlayPause,
  onNext,
  onPrevious,
  onSeek,
  currentTime
}: MusicPlayerProps) {
  const [volume, setVolume] = useState(0.7);
  const [isMuted, setIsMuted] = useState(false);
  const [isVolumeOpen, setIsVolumeOpen] = useState(false);
  const audioRef = useRef<HTMLAudioElement>(null);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  useEffect(() => {
    if (audioRef.current && currentTrack?.audioUrl) {
      audioRef.current.src = currentTrack.audioUrl;
      if (isPlaying) {
        audioRef.current.play().catch(console.error);
      } else {
        audioRef.current.pause();
      }
    }
  }, [currentTrack, isPlaying]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      onSeek(audio.currentTime);
    };

    const handleEnded = () => {
      onNext();
    };

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('ended', handleEnded);
    };
  }, [onSeek, onNext]);

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleSeek = (e: React.MouseEvent<HTMLDivElement>) => {
    if (!currentTrack || !audioRef.current) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - rect.left) / rect.width;
    const newTime = percent * currentTrack.duration;
    
    audioRef.current.currentTime = newTime;
    onSeek(newTime);
  };

  const toggleMute = () => {
    setIsMuted(!isMuted);
  };

  const handleVolumeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    if (newVolume > 0) {
      setIsMuted(false);
    }
  };

  if (!currentTrack) {
    return null;
  }

  const progress = currentTrack.duration > 0 ? (currentTime / currentTrack.duration) * 100 : 0;

  return (
    <>
      <audio ref={audioRef} />
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 px-4 py-3 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Track Info */}
          <div className="flex items-center space-x-4 flex-1 min-w-0">
            <img
              src={currentTrack.imageUrl || 'https://readdy.ai/api/search-image?query=music%20album%20cover%20placeholder%20with%20musical%20notes%2C%20minimalist%20design%2C%20soft%20colors&width=200&height=200&seq=placeholder-cover&orientation=squarish'}
              alt={currentTrack.title}
              className="w-12 h-12 rounded-lg object-cover object-top"
            />
            <div className="min-w-0 flex-1">
              <h4 className="font-medium text-gray-900 truncate">{currentTrack.title}</h4>
              <p className="text-sm text-gray-600 truncate">{currentTrack.artist}</p>
            </div>
            <button className="text-gray-400 hover:text-red-500 transition-colors cursor-pointer">
              <i className="ri-heart-line text-lg"></i>
            </button>
          </div>

          {/* Player Controls */}
          <div className="flex flex-col items-center space-y-2 flex-1 max-w-md">
            <div className="flex items-center space-x-4">
              <button
                onClick={onPrevious}
                className="text-gray-600 hover:text-purple-600 transition-colors cursor-pointer"
              >
                <i className="ri-skip-back-fill text-xl"></i>
              </button>
              
              <Button
                onClick={onPlayPause}
                className="w-10 h-10 rounded-full flex items-center justify-center"
              >
                <i className={`${isPlaying ? 'ri-pause-fill' : 'ri-play-fill'} text-lg`}></i>
              </Button>
              
              <button
                onClick={onNext}
                className="text-gray-600 hover:text-purple-600 transition-colors cursor-pointer"
              >
                <i className="ri-skip-forward-fill text-xl"></i>
              </button>
            </div>

            {/* Progress Bar */}
            <div className="flex items-center space-x-2 w-full">
              <span className="text-xs text-gray-500 w-10 text-right">
                {formatTime(currentTime)}
              </span>
              <div
                className="flex-1 h-1 bg-gray-200 rounded-full cursor-pointer"
                onClick={handleSeek}
              >
                <div
                  className="h-full bg-purple-600 rounded-full transition-all duration-100"
                  style={{ width: `${progress}%` }}
                ></div>
              </div>
              <span className="text-xs text-gray-500 w-10">
                {formatTime(currentTrack.duration)}
              </span>
            </div>
          </div>

          {/* Volume Controls */}
          <div className="flex items-center space-x-4 flex-1 justify-end">
            <button className="text-gray-400 hover:text-purple-600 transition-colors cursor-pointer">
              <i className="ri-playlist-line text-lg"></i>
            </button>
            
            <div className="relative">
              <button
                onClick={() => setIsVolumeOpen(!isVolumeOpen)}
                onMouseEnter={() => setIsVolumeOpen(true)}
                className="text-gray-400 hover:text-purple-600 transition-colors cursor-pointer"
              >
                <i className={`${isMuted || volume === 0 ? 'ri-volume-mute-fill' : volume < 0.5 ? 'ri-volume-down-fill' : 'ri-volume-up-fill'} text-lg`}></i>
              </button>
              
              {isVolumeOpen && (
                <div
                  className="absolute bottom-full right-0 mb-2 bg-white border border-gray-200 rounded-lg shadow-lg p-3"
                  onMouseLeave={() => setIsVolumeOpen(false)}
                >
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={toggleMute}
                      className="text-gray-400 hover:text-purple-600 transition-colors cursor-pointer"
                    >
                      <i className={`${isMuted ? 'ri-volume-mute-fill' : 'ri-volume-up-fill'} text-sm`}></i>
                    </button>
                    <input
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={volume}
                      onChange={handleVolumeChange}
                      className="w-20 h-1 bg-gray-200 rounded-lg appearance-none cursor-pointer"
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
